import React, { useState, useEffect, useMemo } from 'react';
import { db, getCollectionName } from './firebase';
import { usePedidosIW } from './iw/PedidosIW';
import ConciliacaoExames from './exames/ConciliacaoExames';
import {
  collection, query, onSnapshot, orderBy, doc,
  addDoc, updateDoc, deleteDoc, serverTimestamp, setDoc,
} from './firestore-compat';

// ── Constantes ────────────────────────────────────────────────────────────────
// Lista de fallback caso captacao_config/convenios ainda não tenha sido cadastrado
const OPERADORAS_FALLBACK = ['AMIL','BRADESCO','PEND. AMIL','UNIMED baby','Unimed senior','Klini','Unimed Brasil','Unimed Nova Iguaçu','Unimed Leste','Unimed Outros'];
const PRESTADORES  = ['Prontobaby','Externa'];
const SOLICITANTES = ['Núcleo','Plantão','Outro'];
const TIPOS_EXAME  = ['Laboratorial','Imagem','Outro'];

const STATUS_OPTS = [
  'Prévia','Em análise','Inserir no portal','Aguardando pedido','Pendente',
  'Aut. retroativa','Autorizado','Realizado','Negado','Cancelado',
];
const STATUS_COLOR = {
  'Prévia':            { bg:'#ede9fe', text:'#7c3aed' },
  'Autorizado':        { bg:'#dcfce7', text:'#15803d' },
  'Realizado':         { bg:'#d1fae5', text:'#065f46' },
  'Em análise':        { bg:'#fef9c3', text:'#a16207' },
  'Aguardando pedido': { bg:'#fef9c3', text:'#a16207' },
  'Pendente':          { bg:'#fef9c3', text:'#a16207' },
  'Inserir no portal': { bg:'#fef9c3', text:'#a16207' },
  'Negado':            { bg:'#fee2e2', text:'#b91c1c' },
  'Cancelado':         { bg:'#fee2e2', text:'#b91c1c' },
  'Aut. retroativa':   { bg:'#dbeafe', text:'#1d4ed8' },
};
const ANDAMENTO_OPTS = ['Não iniciado','Em andamento','Concluído'];
const ANDAMENTO_COLOR = { 'Não iniciado': '#64748b', 'Em andamento': '#a16207', 'Concluído': '#15803d' };

const PIPELINE_COLUNAS = [
  { key:'previa',     label:'Prévia',         color:'#7c3aed', statuses:['Prévia'] },
  { key:'solicitado', label:'Solicitado',     color:'#d97706', statuses:['Em análise'] },
  { key:'cotacao',    label:'Em Cotação',     color:'#2563eb', statuses:['Inserir no portal','Aguardando pedido'] },
  { key:'aguardando', label:'Ag. Aprovação',  color:'#ea580c', statuses:['Pendente','Aut. retroativa'] },
  { key:'agendado',   label:'Autorizado',     color:'#16a34a', statuses:['Autorizado'] },
  { key:'realizado',  label:'Realizado',      color:'#0d9488', statuses:['Realizado'] },
];
const PIPELINE_TERMINAL = ['Negado','Cancelado'];

const OP_COLOR = {
  'AMIL':          { bg:'#ede9fe', text:'#6d28d9' },
  'BRADESCO':      { bg:'#fee2e2', text:'#b91c1c' },
  'PEND. AMIL':    { bg:'#fde68a', text:'#b45309' },
  'UNIMED baby':   { bg:'#d1fae5', text:'#065f46' },
  'Unimed senior': { bg:'#7c3aed', text:'#fff'    },
  'Klini':         { bg:'#fce7f3', text:'#9d174d' },
  'Unimed Brasil': { bg:'#d1fae5', text:'#065f46' },
};

const P = {
  bg:'#f8fafc', white:'#fff', border:'#e2e8f0', text:'#1e293b',
  muted:'#64748b', primary:'#6366f1', green:'#22c55e',
  red:'#ef4444', amber:'#f59e0b', blue:'#3b82f6', gray:'#94a3b8',
};

const TABS = [
  { k:'pipeline',   l:'📊 Acompanhamento' },
  { k:'agendadas',  l:'Exames Agendados' },
  { k:'concluidos', l:'✅ Concluídos' },
  { k:'conciliacao', l:'🔎 Conciliação (Qualy-X × IW × Quality)' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(s) {
  if (!s || typeof s !== 'string') return '—';
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '—';
  return `${match[3]}/${match[2]}/${match[1]}`;
}
function toSortDate(val) {
  if (!val || typeof val !== 'string') return '';
  const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}
function todayISO() { return new Date().toISOString().split('T')[0]; }

// ── Mini-componentes ──────────────────────────────────────────────────────────
function StatusBadge({ status, small }) {
  const c = STATUS_COLOR[status] || { bg:'#f1f5f9', text:'#64748b' };
  return (
    <span style={{ background:c.bg, color:c.text, padding:small?'2px 7px':'3px 10px',
      borderRadius:20, fontWeight:700, fontSize:small?'0.68rem':'0.74rem', whiteSpace:'nowrap' }}>
      {status || '—'}
    </span>
  );
}
function OpBadge({ op }) {
  const c = OP_COLOR[op] || { bg:'#e2e8f0', text:'#1e293b' };
  return (
    <span style={{ background:c.bg, color:c.text, padding:'2px 9px', borderRadius:20,
      fontWeight:700, fontSize:'0.72rem', whiteSpace:'nowrap' }}>{op || '—'}</span>
  );
}
function KpiCard({ label, value, color, onClick, sub }) {
  return (
    <div onClick={onClick} style={{ background:P.white, border:`1px solid ${P.border}`, borderRadius:10,
      padding:'12px 18px', minWidth:130, flex:1, cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize:'0.68rem', fontWeight:700, color:P.muted, textTransform:'uppercase', letterSpacing:.5 }}>{label}</div>
      <div style={{ fontSize:'1.8rem', fontWeight:800, color, lineHeight:1.1, margin:'4px 0 0' }}>{value}</div>
      {sub && <div style={{ fontSize:'0.68rem', color:P.muted, marginTop:2 }}>{sub}</div>}
    </div>
  );
}
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:P.white, borderRadius:14, width:'100%', maxWidth:wide?800:560,
        maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.25)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'16px 22px', borderBottom:`1px solid ${P.border}`,
          position:'sticky', top:0, background:P.white, zIndex:1 }}>
          <h2 style={{ margin:0, fontSize:'1rem', fontWeight:800, color:P.text }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'1.3rem', cursor:'pointer', color:P.muted }}>✕</button>
        </div>
        <div style={{ padding:'20px 22px' }}>{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:'0.71rem', fontWeight:700, color:P.muted,
        textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
        {label}{required && <span style={{ color:P.red }}> *</span>}
      </label>
      {children}
    </div>
  );
}
const inp = {
  width:'100%', padding:'8px 11px', border:`1px solid ${P.border}`, borderRadius:8,
  fontSize:'0.85rem', background:P.white, color:P.text, boxSizing:'border-box', outline:'none',
};
function Sel({ value, onChange, options, placeholder }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={inp}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function Btn({ children, onClick, disabled, color = P.primary, small }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ background:disabled?P.border:color, color:'#fff', border:'none',
        borderRadius:8, padding:small?'5px 12px':'8px 18px',
        fontWeight:700, fontSize:small?'0.78rem':'0.84rem',
        cursor:disabled?'default':'pointer', opacity:disabled?.6:1 }}>
      {children}
    </button>
  );
}
function GhostBtn({ children, onClick, small }) {
  return (
    <button onClick={onClick}
      style={{ background:'none', border:`1px solid ${P.border}`, color:P.muted,
        borderRadius:8, padding:small?'4px 10px':'7px 14px',
        fontWeight:600, fontSize:small?'0.78rem':'0.82rem', cursor:'pointer' }}>
      {children}
    </button>
  );
}
function TH({ children }) {
  return <th style={{ padding:'9px 12px', textAlign:'left', fontSize:'0.68rem', fontWeight:700,
    color:P.muted, textTransform:'uppercase', letterSpacing:.5, borderBottom:`1px solid ${P.border}`,
    whiteSpace:'nowrap' }}>{children}</th>;
}

// ── Pipeline Kanban ────────────────────────────────────────────────────────────
function PipelineCard({ r, onChangeStatus }) {
  const [menu, setMenu] = useState(false);
  const sc = STATUS_COLOR[r.status] || { bg:'#f1f5f9', text:'#64748b' };
  return (
    <div style={{ background:'#fff', borderRadius:10, border:'1px solid #e2e8f0', padding:'10px 12px', marginBottom:8,
      boxShadow:'0 1px 3px rgba(0,0,0,0.05)', borderLeft:`3px solid ${sc.text}` }}>
      <div style={{ fontWeight:700, color:'#1e293b', fontSize:'0.85rem', marginBottom:4 }}>{r.paciente}</div>
      {r.operadora && <OpBadge op={r.operadora} />}
      {r.exame && <span style={{ marginLeft:4, fontSize:'0.7rem', color:'#64748b' }}>{r.exame}</span>}
      {(r.data||r.horario) && (
        <div style={{ color:'#64748b', fontSize:'0.75rem', marginTop:4 }}>{fmtDate(r.data)}{r.horario?` · ${r.horario}`:''}</div>
      )}
      <div style={{ marginTop:8, position:'relative' }}>
        <button onClick={()=>setMenu(v=>!v)} style={{ width:'100%', textAlign:'left', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:'0.72rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <StatusBadge status={r.status} small />
          <span style={{ color:'#94a3b8' }}>▾</span>
        </button>
        {menu && (
          <div style={{ position:'absolute', bottom:'calc(100% + 4px)', left:0, right:0, zIndex:200, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', maxHeight:220, overflowY:'auto' }}>
            {STATUS_OPTS.filter(s=>s!==r.status).map(s=>(
              <div key={s} onClick={()=>{ onChangeStatus(r,s); setMenu(false); }}
                style={{ padding:'7px 12px', cursor:'pointer', fontSize:'0.8rem' }}
                onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                onMouseLeave={e=>e.currentTarget.style.background=''}>
                <StatusBadge status={s} small />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TabPipeline({ data, col, operadoras = OPERADORAS_FALLBACK }) {
  const [search, setSearch] = useState('');
  const [fOp,    setFOp]    = useState('');

  const filtered = useMemo(() => {
    let r = data;
    if (search) r = r.filter(x => x.paciente?.toLowerCase().includes(search.toLowerCase()));
    if (fOp)    r = r.filter(x => x.operadora === fOp);
    return r;
  }, [data, search, fOp]);

  const byColuna = useMemo(() => {
    const map = {};
    PIPELINE_COLUNAS.forEach(c => { map[c.key] = []; });
    filtered.forEach(r => {
      const found = PIPELINE_COLUNAS.find(c => c.statuses.includes(r.status));
      if (found) map[found.key].push(r);
    });
    return map;
  }, [filtered]);

  const terminal = useMemo(() => filtered.filter(r => PIPELINE_TERMINAL.includes(r.status)), [filtered]);

  const kpis = useMemo(() => ({
    total:     data.length,
    previa:    data.filter(r=>r.status==='Prévia').length,
    agendado:  data.filter(r=>r.status==='Autorizado').length,
    realizado: data.filter(r=>r.status==='Realizado').length,
    cancelado: data.filter(r=>PIPELINE_TERMINAL.includes(r.status)).length,
  }), [data]);

  const changeStatus = async (r, status) => {
    await updateDoc(doc(db, col, r.id), { status, updatedAt: serverTimestamp() });
  };

  return (
    <div>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:20 }}>
        <KpiCard label="Total"           value={kpis.total}     color={P.muted} />
        <KpiCard label="Prévias"         value={kpis.previa}    color='#7c3aed' />
        <KpiCard label="Autorizados"     value={kpis.agendado}  color={P.green} />
        <KpiCard label="Realizados"      value={kpis.realizado} color='#0d9488' />
        <KpiCard label="Canceladas/Neg." value={kpis.cancelado} color={P.red}   />
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input placeholder="Buscar paciente…" value={search} onChange={e=>setSearch(e.target.value)}
          style={{ border:`1px solid ${P.border}`, borderRadius:8, padding:'7px 12px', fontSize:'0.84rem', flex:1, minWidth:160, outline:'none' }} />
        <select value={fOp} onChange={e=>setFOp(e.target.value)}
          style={{ border:`1px solid ${P.border}`, borderRadius:8, padding:'7px 10px', fontSize:'0.84rem', color:fOp?P.text:P.muted }}>
          <option value="">Todas operadoras</option>
          {operadoras.map(o=><option key={o} value={o}>{o}</option>)}
        </select>
        {(search||fOp) && (
          <button onClick={()=>{ setSearch(''); setFOp(''); }}
            style={{ background:'none', border:`1px solid ${P.border}`, borderRadius:8, padding:'7px 12px', cursor:'pointer', fontSize:'0.82rem', color:P.muted }}>
            Limpar
          </button>
        )}
      </div>

      <div style={{ display:'flex', gap:12, overflowX:'auto', paddingBottom:16 }}>
        {PIPELINE_COLUNAS.map(coluna => (
          <div key={coluna.key} style={{ minWidth:220, flex:'0 0 220px' }}>
            <div style={{ background:coluna.color, color:'#fff', fontWeight:700, fontSize:'0.78rem', padding:'8px 12px', borderRadius:'8px 8px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>{coluna.label}</span>
              <span style={{ background:'rgba(255,255,255,0.25)', borderRadius:10, padding:'1px 8px', fontWeight:800 }}>{byColuna[coluna.key]?.length||0}</span>
            </div>
            <div style={{ background:'#f8fafc', borderRadius:'0 0 8px 8px', border:'1px solid #e2e8f0', borderTop:'none', padding:'10px 8px', minHeight:100 }}>
              {(byColuna[coluna.key]||[]).length === 0
                ? <div style={{ color:'#cbd5e1', fontSize:'0.75rem', textAlign:'center', padding:'24px 0' }}>Vazio</div>
                : (byColuna[coluna.key]||[]).map(r => <PipelineCard key={r.id} r={r} onChangeStatus={changeStatus} />)
              }
            </div>
          </div>
        ))}
      </div>

      {terminal.length > 0 && (
        <div style={{ marginTop:20 }}>
          <div style={{ fontSize:'0.78rem', fontWeight:700, color:P.muted, textTransform:'uppercase', letterSpacing:.5, marginBottom:10 }}>
            Cancelados / Negados ({terminal.length})
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {terminal.map(r=>(
              <div key={r.id} style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 14px', fontSize:'0.8rem' }}>
                <strong style={{ color:P.text }}>{r.paciente}</strong>
                <span style={{ marginLeft:8 }}><StatusBadge status={r.status} small /></span>
                {r.data&&<span style={{ marginLeft:8, color:P.muted }}>{fmtDate(r.data)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab: Exames Agendados ───────────────────────────────────────────────────────
const EMPTY_EXAME = {
  paciente:'', idade:'', exame:'', tipo_exame:'Laboratorial', operadora:'', data:'', horario:'',
  solicitante:'Núcleo', prestador:'Prontobaby', status:'Em análise',
  local_realizacao:'', protocolo:'', observacoes:'',
  andamento:'Não iniciado', aditivo_iw:'', input_impressos:'',
};

function TabAgendados({ data, col, onlyConcluidos = false, operadoras = OPERADORAS_FALLBACK, examesDisponiveis = [] }) {
  const [subTab,   setSubTab]   = useState('agendados');
  const [search,   setSearch]   = useState('');
  const [fStatus,  setFStatus]  = useState('');
  const [fOp,      setFOp]      = useState('');
  const [fDIni,    setFDIni]    = useState('');
  const [fDFim,    setFDFim]    = useState('');
  const [modal,    setModal]    = useState(false);
  const [edit,     setEdit]     = useState(null);
  const [form,     setForm]     = useState(EMPTY_EXAME);
  const [saving,   setSaving]   = useState(false);
  const [delId,    setDelId]    = useState(null);
  const [inlineSt, setInlineSt] = useState(null);
  const [showHoje, setShowHoje] = useState(false);
  const [gerenciarExames, setGerenciarExames] = useState(false);

  const openNew  = () => { setForm(EMPTY_EXAME); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({ ...EMPTY_EXAME, ...r }); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const save = async () => {
    if (!form.paciente.trim()) return;
    setSaving(true);
    try {
      if (edit) await updateDoc(doc(db, col, edit.id), { ...form, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db, col), { ...form, criadoEm: serverTimestamp() });
      close();
    } catch(e) { console.error(e); alert('Erro ao salvar: ' + (e.message || JSON.stringify(e))); }
    setSaving(false);
  };

  const quickStatus = async (r, status) => {
    await updateDoc(doc(db, col, r.id), { status, updatedAt: serverTimestamp() });
    setInlineSt(null);
  };

  const rows = useMemo(() => {
    let r = data;
    if (search)  r = r.filter(x => ((x.paciente||'')+(x.exame||'')+(x.observacoes||'')).toLowerCase().includes(search.toLowerCase()));
    if (fStatus) r = r.filter(x => x.status    === fStatus);
    if (fOp)     r = r.filter(x => x.operadora === fOp);
    if (fDIni)   r = r.filter(x => x.data >= fDIni);
    if (fDFim)   r = r.filter(x => x.data <= fDFim);
    return [...r].sort((a, b) => {
      const da = toSortDate(a.data); const db_ = toSortDate(b.data);
      if (!da && !db_) return 0; if (!da) return 1; if (!db_) return -1;
      return da < db_ ? -1 : da > db_ ? 1 : 0;
    });
  }, [data, search, fStatus, fOp, fDIni, fDFim]);

  // Concluído = exame realizado OU tarefa concluída (andamento) — antes os de andamento
  // "Concluído" continuavam em Agendados
  const concluido = r => r.status === 'Realizado' || r.andamento === 'Concluído';
  const rowsAgendados  = useMemo(() => rows.filter(r => !concluido(r)), [rows]);
  const rowsConcluidos = useMemo(() => rows.filter(concluido).sort((a, b) => {
    const da = toSortDate(a.data); const db_ = toSortDate(b.data);
    if (!da && !db_) return 0; if (!da) return 1; if (!db_) return -1;
    return da > db_ ? -1 : da < db_ ? 1 : 0;
  }), [rows]);
  // Concluídos (aba top-level) = mesma regra (realizado ou andamento concluído)
  const rowsConcluidosAndamento = useMemo(() => rows.filter(concluido).sort((a, b) => {
    const da = toSortDate(a.data); const db_ = toSortDate(b.data);
    if (!da && !db_) return 0; if (!da) return 1; if (!db_) return -1;
    return da > db_ ? -1 : da < db_ ? 1 : 0;
  }), [rows]);
  // Pedidos de exame lançados no IW que ainda não estão aqui: entram no topo de Agendados,
  // com a etiqueta "Pedido no IW" e o botão "Lançar" (abre o formulário já preenchido)
  const iw = usePedidosIW({ tipo: 'exame', existentes: data, operadoras });
  const linhasIW = useMemo(() => (onlyConcluidos || subTab !== 'agendados' ? [] : iw.faltam.filter(p =>
    (!search || `${p.paciente} ${p.pedido}`.toLowerCase().includes(search.toLowerCase()))
    && (!fOp || p.operadoraLista === fOp) && !fStatus
  ).map(p => ({ ...p, _iw: true, id: `iw-${p.evolucao}` }))), [iw.faltam, onlyConcluidos, subTab, search, fOp, fStatus]);
  const displayRows = onlyConcluidos ? rowsConcluidosAndamento : (subTab === 'concluidos' ? rowsConcluidos : [...linhasIW, ...rowsAgendados]);

  const hoje = todayISO();
  const kpis = useMemo(() => ({
    total:     rowsAgendados.length,
    autorizado:rowsAgendados.filter(x => x.status === 'Autorizado').length,
    pendente:  rowsAgendados.filter(x => ['Em análise','Pendente','Aguardando pedido','Inserir no portal'].includes(x.status)).length,
    cancelado: rowsAgendados.filter(x => ['Cancelado','Negado'].includes(x.status)).length,
    hoje:      data.filter(x => x.data === hoje && x.status !== 'Realizado'),
  }), [rowsAgendados, data, hoje]);

  const hasF = search||fStatus||fOp||fDIni||fDFim;
  const clrF = () => { setSearch(''); setFStatus(''); setFOp(''); setFDIni(''); setFDFim(''); };

  return (
    <div>
      {onlyConcluidos ? (
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
          <KpiCard label="✅ Concluídos" value={rowsConcluidosAndamento.length} color="#15803d" />
        </div>
      ) : (
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
          <KpiCard label="Total"       value={kpis.total}       color={P.muted} />
          <KpiCard label="Autorizados" value={kpis.autorizado}  color={P.green} />
          <KpiCard label="Pendentes"   value={kpis.pendente}    color={P.amber} />
          <KpiCard label="Cancelados"  value={kpis.cancelado}   color={P.red}   />
          <KpiCard label="🗂 Pedidos no IW" value={iw.faltam.length} color="#065f46" sub="ainda não lançados aqui" />
          <KpiCard label="🔬 Hoje"    value={kpis.hoje.length} color='#0d9488'
            sub={kpis.hoje.length ? 'ver lista →' : 'nenhum hoje'}
            onClick={kpis.hoje.length ? ()=>setShowHoje(true) : undefined} />
        </div>
      )}

      {/* Sub-tabs: Agendados / Concluídos (não se aplica na aba top-level "Concluídos") */}
      {!onlyConcluidos && (
      <div style={{ display:'flex', gap:0, marginBottom:14, background:'#f1f5f9', borderRadius:10, padding:3, width:'fit-content' }}>
        {[
          { k:'agendados',  l:'⚡ Agendados',  count: rowsAgendados.length,  color:'#6366f1' },
          { k:'concluidos', l:'✅ Concluídos', count: rowsConcluidos.length, color:'#0d9488' },
        ].map(({ k, l, count, color }) => (
          <button key={k} onClick={() => setSubTab(k)} style={{
            border: 'none', borderRadius: 8, padding: '6px 18px', cursor: 'pointer',
            fontSize: '0.82rem', fontWeight: 700, transition: 'all 0.15s',
            background: subTab === k ? P.white : 'transparent',
            color: subTab === k ? color : '#94a3b8',
            boxShadow: subTab === k ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
          }}>
            {l}
            <span style={{ marginLeft: 6, background: subTab === k ? color : '#e2e8f0', color: subTab === k ? '#fff' : '#94a3b8',
              borderRadius: 20, padding: '1px 7px', fontSize: '0.7rem', fontWeight: 700 }}>
              {count}
            </span>
          </button>
        ))}
      </div>
      )}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        {!onlyConcluidos && subTab === 'agendados' && <Btn onClick={openNew}>+ Novo Exame</Btn>}
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar paciente ou exame…"
          style={{ ...inp, maxWidth:260, flex:1 }} />
        <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{ ...inp, maxWidth:170 }}>
          <option value="">Todos os status</option>
          {STATUS_OPTS.filter(s => onlyConcluidos ? true : (subTab === 'concluidos' ? s === 'Realizado' : s !== 'Realizado')).map(s=><option key={s}>{s}</option>)}
        </select>
        <select value={fOp} onChange={e=>setFOp(e.target.value)} style={{ ...inp, maxWidth:160 }}>
          <option value="">Todas operadoras</option>
          {operadoras.map(o=><option key={o}>{o}</option>)}
        </select>
        <input type="date" value={fDIni} onChange={e=>setFDIni(e.target.value)} style={{ ...inp, maxWidth:148 }} />
        <input type="date" value={fDFim} onChange={e=>setFDFim(e.target.value)} style={{ ...inp, maxWidth:148 }} />
        {hasF && <GhostBtn onClick={clrF}>✕ Limpar</GhostBtn>}
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:P.muted }}>{displayRows.length} registro{displayRows.length!==1?'s':''}</span>
      </div>

      <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}` }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.75rem', tableLayout:'fixed' }}>
          <colgroup>
            <col style={{ width:'13%' }} />{/* Paciente */}
            <col style={{ width:'13%' }} />{/* Exame */}
            <col style={{ width:'8%'  }} />{/* Tipo */}
            <col style={{ width:'9%'  }} />{/* Operadora */}
            <col style={{ width:'8%'  }} />{/* Data/Hora */}
            <col style={{ width:'8%'  }} />{/* Solic./Prest. */}
            <col style={{ width:'9%'  }} />{/* Status */}
            <col style={{ width:'8%'  }} />{/* Andamento */}
            <col style={{ width:'7%'  }} />{/* Aditivo IW */}
            <col style={{ width:'7%'  }} />{/* Input Impressos */}
            <col style={{ width:'10%' }} />{/* Local */}
            <col style={{ width:'6%'  }} />{/* Ações */}
          </colgroup>
          <thead><tr>
            <TH>Paciente</TH><TH>Exame</TH><TH>Tipo</TH><TH>Operadora</TH><TH>Data / Hora</TH>
            <TH>Solic. / Prest.</TH><TH>Status</TH><TH>Andamento</TH>
            <TH>Aditivo no IW</TH><TH>Input em Impressos</TH>
            <TH>Local</TH><TH>Ações</TH>
          </tr></thead>
          <tbody>
            {displayRows.length === 0 && <tr><td colSpan={12} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum registro.</td></tr>}
            {displayRows.map((r,i) => r._iw ? (
              <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`, background:'#f0fdf4' }}>
                <td style={{ padding:'6px 8px', fontWeight:600, color:P.text }} title={r.paciente}>
                  <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.paciente}</div>
                  <span style={{ fontSize:'0.66rem', fontWeight:800, color:'#065f46', background:'#dcfce7', borderRadius:999, padding:'1px 7px' }}>🗂 Pedido no IW</span>
                </td>
                <td style={{ padding:'6px 8px', color:P.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={`${r.pedido}

Justificativa: ${r.justificativa}`}>{r.pedido||'—'}</td>
                <td style={{ padding:'6px 8px', color:P.muted }}>—</td>
                <td style={{ padding:'6px 8px' }}>{r.operadoraLista ? <OpBadge op={r.operadoraLista} /> : <span style={{ fontSize:'0.72rem', color:P.muted }}>{r.operadora}</span>}</td>
                <td style={{ padding:'6px 8px', color:P.text, whiteSpace:'nowrap' }}><div>{fmtDate(r.data)||'—'}</div><div style={{ color:P.muted, fontSize:'0.7rem' }}>pedido no IW</div></td>
                <td style={{ padding:'6px 8px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  <div style={{ color:P.text }} title={r.profissional}>{r.profissional||'—'}</div>
                  <div style={{ color:P.muted, fontSize:'0.7rem' }}>{r.especialidade||''}</div>
                </td>
                <td style={{ padding:'6px 8px' }}><span style={{ fontSize:'0.72rem', fontWeight:700, color:'#065f46' }} title="Os pedidos do IW entram sozinhos nesta lista (a cada 30 min)">Entra sozinho</span></td>
                <td style={{ padding:'6px 8px', color:P.muted }}>—</td>
                <td style={{ padding:'6px 8px', fontSize:'0.72rem', fontWeight:700, color: r.aditivo ? '#15803d' : P.muted }} title={r.aditivo ? `Aditivo no IW em ${fmtDate(r.aditivo.criadoEm)} · autorização interna ${r.aditivo.autorizacaoInterna}/3 · operadora ${r.aditivo.autorizacaoOperadora}` : ''}>{r.aditivo ? 'Sim (IW)' : '—'}</td>
                <td style={{ padding:'6px 8px', color:P.muted }}>—</td>
                <td style={{ padding:'6px 8px', color:P.muted }}>—</td>
                <td style={{ padding:'6px 8px', color:P.muted }}>—</td>
              </tr>
            ) : (
              <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`, background:i%2===0?'transparent':'#fafafa' }}>
                <td style={{ padding:'6px 8px', fontWeight:600, color:P.text }} title={r.paciente}>
                  <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.paciente}</div>
                  {r.origem === 'IW' && <span title={`Pedido no IW em ${fmtDate(r.iw_data_pedido)} por ${r.iw_profissional || ''}`} style={{ fontSize:'0.66rem', fontWeight:800, color:'#065f46', background:'#dcfce7', borderRadius:999, padding:'1px 7px' }}>🗂 Veio do IW</span>}
                </td>
                <td style={{ padding:'6px 8px', color:P.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.exame}>{r.exame||'—'}</td>
                <td style={{ padding:'6px 8px', color:P.muted }}>{r.tipo_exame||'—'}</td>
                <td style={{ padding:'6px 8px' }}><OpBadge op={r.operadora} /></td>
                <td style={{ padding:'6px 8px', color:P.text, whiteSpace:'nowrap' }}>
                  <div>{fmtDate(r.data)||'—'}</div>
                  <div style={{ color:P.muted, fontSize:'0.7rem' }}>{r.horario||'—'}</div>
                </td>
                <td style={{ padding:'6px 8px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  <div style={{ color:P.text }}>{r.solicitante||'—'}</div>
                  <div style={{ color:P.muted, fontSize:'0.7rem' }}>{r.prestador||'—'}</div>
                </td>
                <td style={{ padding:'6px 8px', position:'relative' }}>
                  <div onClick={()=>setInlineSt(inlineSt===r.id?null:r.id)} style={{ cursor:'pointer' }}>
                    <StatusBadge status={r.status} />
                  </div>
                  {inlineSt === r.id && (
                    <div style={{ position:'absolute', top:'100%', left:0, zIndex:100, background:P.white,
                      border:`1px solid ${P.border}`, borderRadius:10, padding:8,
                      boxShadow:'0 8px 24px rgba(0,0,0,.12)', minWidth:200 }}>
                      {STATUS_OPTS.map(s => (
                        <div key={s} onClick={()=>quickStatus(r,s)}
                          style={{ padding:'6px 10px', cursor:'pointer', borderRadius:7 }}
                          onMouseEnter={e=>e.currentTarget.style.background='#f1f5f9'}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <StatusBadge status={s} small />
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ padding:'4px 6px' }}>
                  <select
                    value={r.andamento || 'Não iniciado'}
                    onChange={async e => { await updateDoc(doc(db, col, r.id), { andamento: e.target.value, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6,
                      padding:'3px 4px', color: ANDAMENTO_COLOR[r.andamento || 'Não iniciado'],
                      fontWeight:700, background:P.white, cursor:'pointer' }}>
                    {ANDAMENTO_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </td>
                <td style={{ padding:'4px 6px' }}>
                  <select
                    value={r.aditivo_iw || ''}
                    onChange={async e => { await updateDoc(doc(db, col, r.id), { aditivo_iw: e.target.value, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6,
                      padding:'3px 4px', color: r.aditivo_iw==='Sim'?'#15803d':r.aditivo_iw==='Não'?'#b91c1c':P.muted,
                      fontWeight: r.aditivo_iw?700:400, background:P.white, cursor:'pointer' }}>
                    <option value="">—</option>
                    <option value="Sim">Sim</option>
                    <option value="Não">Não</option>
                    <option value="N/A">N/A</option>
                  </select>
                </td>
                <td style={{ padding:'4px 6px' }}>
                  <select
                    value={r.input_impressos || ''}
                    onChange={async e => { await updateDoc(doc(db, col, r.id), { input_impressos: e.target.value, updatedAt: serverTimestamp() }); }}
                    style={{ width:'100%', fontSize:'0.72rem', border:`1px solid ${P.border}`, borderRadius:6,
                      padding:'3px 4px', color: r.input_impressos==='Sim'?'#15803d':r.input_impressos==='Não'?'#b91c1c':P.muted,
                      fontWeight: r.input_impressos?700:400, background:P.white, cursor:'pointer' }}>
                    <option value="">—</option>
                    <option value="Sim">Sim</option>
                    <option value="Não">Não</option>
                    <option value="N/A">N/A</option>
                  </select>
                </td>
                <td style={{ padding:'6px 8px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.local_realizacao}>{r.local_realizacao||'—'}</td>
                <td style={{ padding:'6px 8px', whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                    <GhostBtn small onClick={()=>setDelId(r.id)}>🗑</GhostBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showHoje && (
        <Modal title={`🔬 Exames de Hoje — ${new Date().toLocaleDateString('pt-BR')}`} onClose={()=>setShowHoje(false)} wide>
          {kpis.hoje.length === 0 ? (
            <p style={{ color:P.muted, textAlign:'center', padding:'20px 0' }}>Nenhum exame agendado para hoje.</p>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
              <thead>
                <tr style={{ borderBottom:`2px solid ${P.border}` }}>
                  {['Horário','Paciente','Exame','Operadora','Status'].map(h => (
                    <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:P.muted, fontWeight:700, fontSize:'0.69rem', textTransform:'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...kpis.hoje].sort((a,b)=>(a.horario||'').localeCompare(b.horario||'')).map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`, background: i%2===0?'transparent':'#fafafa' }}>
                    <td style={{ padding:'7px 10px', fontWeight:700, color:'#0d9488' }}>{r.horario||'—'}</td>
                    <td style={{ padding:'7px 10px', fontWeight:600, color:P.text }}>{r.paciente}{r.idade?` (${r.idade}a)`:''}</td>
                    <td style={{ padding:'7px 10px', color:P.text }}>{r.exame||'—'}</td>
                    <td style={{ padding:'7px 10px' }}><OpBadge op={r.operadora} /></td>
                    <td style={{ padding:'7px 10px' }}><StatusBadge status={r.status} small /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}

      {modal && (
        <Modal title={edit?'Editar Exame':'Novo Exame'} onClose={close} wide>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Paciente" required>
                <input value={form.paciente} onChange={e=>setForm(f=>({...f,paciente:e.target.value}))} placeholder="Nome completo do paciente" style={inp} />
              </Field>
            </div>
            <Field label="Idade (anos)">
              <input type="number" min="0" max="130" value={form.idade||''} onChange={e=>setForm(f=>({...f,idade:e.target.value}))} placeholder="Ex.: 65" style={inp} />
            </Field>
            <Field label="Tipo de Exame"><Sel value={form.tipo_exame} onChange={v=>setForm(f=>({...f,tipo_exame:v}))} options={TIPOS_EXAME} /></Field>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Exame" required>
                <div style={{ display:'flex', gap:8 }}>
                  <input list="exames-disponiveis-list" value={form.exame} onChange={e=>setForm(f=>({...f,exame:e.target.value}))} placeholder="Digite ou selecione um exame cadastrado…" style={{ ...inp, flex:1 }} />
                  <GhostBtn small onClick={()=>setGerenciarExames(true)}>⚙️ Exames</GhostBtn>
                </div>
                <datalist id="exames-disponiveis-list">
                  {examesDisponiveis.map(ex => <option key={ex} value={ex} />)}
                </datalist>
              </Field>
            </div>
            <Field label="Operadora" required><Sel value={form.operadora} onChange={v=>setForm(f=>({...f,operadora:v}))} options={operadoras} placeholder="Selecionar…" /></Field>
            <Field label="Data do exame"><input type="date" value={form.data} onChange={e=>setForm(f=>({...f,data:e.target.value}))} style={inp} /></Field>
            <Field label="Horário"><input type="time" value={form.horario} onChange={e=>setForm(f=>({...f,horario:e.target.value}))} style={inp} /></Field>
            <Field label="Solicitante"><Sel value={form.solicitante} onChange={v=>setForm(f=>({...f,solicitante:v}))} options={SOLICITANTES} /></Field>
            <Field label="Prestador"><Sel value={form.prestador} onChange={v=>setForm(f=>({...f,prestador:v}))} options={PRESTADORES} /></Field>
            <Field label="Status"><Sel value={form.status} onChange={v=>setForm(f=>({...f,status:v}))} options={STATUS_OPTS} /></Field>
            <Field label="Local de realização">
              <input value={form.local_realizacao} onChange={e=>setForm(f=>({...f,local_realizacao:e.target.value}))} placeholder="Ex.: Domicílio, Laboratório X, Clínica Y…" style={inp} />
            </Field>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Protocolo / Nº autorização">
                <input value={form.protocolo} onChange={e=>setForm(f=>({...f,protocolo:e.target.value}))} placeholder="Nº do protocolo ou autorização da operadora" style={inp} />
              </Field>
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Observações">
                <textarea value={form.observacoes} onChange={e=>setForm(f=>({...f,observacoes:e.target.value}))} rows={3} placeholder="Detalhes, resultado, telefone de contato…" style={{ ...inp, resize:'vertical' }} />
              </Field>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.paciente.trim()}>{saving?'Salvando…':'Salvar exame'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}

      {delId && (
        <Modal title="Confirmar exclusão" onClose={()=>setDelId(null)}>
          <p style={{ color:P.text, marginBottom:20 }}>Deseja excluir este registro? Esta ação não pode ser desfeita.</p>
          <div style={{ display:'flex', gap:10 }}>
            <Btn color={P.red} onClick={()=>{ deleteDoc(doc(db,col,delId)); setDelId(null); }}>Excluir</Btn>
            <GhostBtn onClick={()=>setDelId(null)}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}

      {gerenciarExames && (
        <GerenciarExamesModal examesDisponiveis={examesDisponiveis} onClose={()=>setGerenciarExames(false)} />
      )}
    </div>
  );
}

// ── Modal: Gerenciar lista de exames disponíveis ────────────────────────────────
function GerenciarExamesModal({ examesDisponiveis, onClose }) {
  const [novoExame, setNovoExame] = useState('');
  const [saving, setSaving] = useState(false);
  const ref = doc(db, getCollectionName('config_exames'), 'main');

  const salvarLista = async lista => {
    setSaving(true);
    try { await setDoc(ref, { lista }); } catch(e) { alert('Erro ao salvar: ' + e.message); }
    setSaving(false);
  };

  const adicionar = async () => {
    const nome = novoExame.trim();
    if (!nome) return;
    if (examesDisponiveis.some(e => e.toLowerCase() === nome.toLowerCase())) {
      alert('Esse exame já está cadastrado.');
      return;
    }
    await salvarLista([...examesDisponiveis, nome].sort());
    setNovoExame('');
  };

  const remover = async nome => {
    await salvarLista(examesDisponiveis.filter(e => e !== nome));
  };

  return (
    <Modal title="⚙️ Gerenciar Exames Disponíveis" onClose={onClose}>
      <p style={{ color:P.muted, fontSize:'0.82rem', marginBottom:14 }}>
        Cadastre aqui os exames mais usados pra aparecerem como sugestão ao digitar. Você continua podendo digitar qualquer exame que não estiver na lista.
      </p>
      <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:16, maxHeight:280, overflowY:'auto' }}>
        {examesDisponiveis.length === 0 && (
          <p style={{ color:P.muted, fontSize:'0.8rem', textAlign:'center', padding:'12px 0' }}>Nenhum exame cadastrado ainda.</p>
        )}
        {examesDisponiveis.map(ex => (
          <div key={ex} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
            padding:'6px 10px', borderRadius:8, border:`1px solid ${P.border}` }}>
            <span style={{ fontSize:'0.85rem', color:P.text }}>{ex}</span>
            <GhostBtn small onClick={()=>remover(ex)}>🗑</GhostBtn>
          </div>
        ))}
      </div>
      <div style={{ borderTop:`1px solid ${P.border}`, paddingTop:14, display:'flex', gap:8 }}>
        <input value={novoExame} onChange={e=>setNovoExame(e.target.value)} placeholder="Ex.: Hemograma completo" style={{ ...inp, flex:1 }}
          onKeyDown={e=>e.key==='Enter' && adicionar()} />
        <Btn onClick={adicionar} disabled={saving||!novoExame.trim()}>{saving?'Salvando…':'+ Adicionar'}</Btn>
      </div>
    </Modal>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function GestaoSolicitacaoExames() {
  const [tab, setTab] = useState('agendadas');
  const [data, setData] = useState([]);
  const [operadoras, setOperadoras] = useState(OPERADORAS_FALLBACK);
  const [examesDisponiveis, setExamesDisponiveis] = useState([]);
  const col = getCollectionName('exames_solicitacoes');

  useEffect(() => {
    const q = query(collection(db, col), orderBy('criadoEm','desc'));
    return onSnapshot(q, snap => setData(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [col]);

  // Convênios cadastrados na Captação (Configurações → Captação → Convênios) — mesma fonte usada lá
  useEffect(() => {
    const ref = doc(db, getCollectionName('captacao_config'), 'convenios');
    return onSnapshot(ref, snap => {
      const lista = snap.exists() ? (snap.data().lista || []) : [];
      if (lista.length) setOperadoras(lista);
    }, () => {});
  }, []);

  // Lista de exames disponíveis pra seleção rápida (cadastrável nesta própria tela)
  useEffect(() => {
    const ref = doc(db, getCollectionName('config_exames'), 'main');
    return onSnapshot(ref, snap => {
      setExamesDisponiveis(snap.exists() ? (snap.data().lista || []) : []);
    }, () => {});
  }, []);

  const tabStyle = k => ({
    padding:'8px 16px', borderRadius:'8px 8px 0 0',
    border:`1px solid ${P.border}`, borderBottom: k===tab ? `2px solid ${P.primary}` : `1px solid ${P.border}`,
    background: k===tab ? P.white : '#f1f5f9',
    color: k===tab ? P.primary : P.muted,
    fontWeight: k===tab ? 800 : 600, fontSize:'0.8rem', cursor:'pointer',
    position:'relative', top:'1px', whiteSpace:'nowrap',
  });

  return (
    <div style={{ background:P.bg, minHeight:'100vh', padding:'24px 24px 60px' }}>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ margin:0, fontSize:'1.4rem', fontWeight:800, color:P.text }}>🔬 Gestão de Solicitação de Exames</h1>
        <p style={{ margin:'4px 0 0', color:P.muted, fontSize:'0.83rem' }}>Solicitações de exames laboratoriais e de imagem dos pacientes homecare</p>
      </div>

      <div style={{ display:'flex', flexWrap:'wrap', gap:4, borderBottom:`1px solid ${P.border}`, marginBottom:0 }}>
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={tabStyle(t.k)}>{t.l}</button>
        ))}
      </div>

      <div style={{ background:P.white, border:`1px solid ${P.border}`, borderTop:'none', borderRadius:'0 0 12px 12px', padding:'22px 20px' }}>
        {tab==='pipeline'   && <TabPipeline   data={data} col={col} operadoras={operadoras} />}
        {tab==='agendadas'  && <TabAgendados  data={data} col={col} operadoras={operadoras} examesDisponiveis={examesDisponiveis} />}
        {tab==='concluidos' && <TabAgendados  data={data} col={col} operadoras={operadoras} examesDisponiveis={examesDisponiveis} onlyConcluidos />}
        {tab==='conciliacao' && <ConciliacaoExames />}
      </div>
    </div>
  );
}
